import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { setHeader, setRoutes, setUsername } from '../core/ui/shell';
import { useIdentity } from 'src/core/services/authentication';
import { Menu, Card, Row, Spin } from 'antd';
import {
  UserOutlined,
  AuditOutlined,
  TeamOutlined,
  IdcardOutlined,
  BankOutlined,
  FileProtectOutlined,
  FileDoneOutlined,
  MonitorOutlined,
  LockOutlined,
  FileTextOutlined,
  KeyOutlined,
  FundOutlined,
  MobileOutlined,
  CalendarOutlined,
  YoutubeOutlined,
} from '@ant-design/icons';
import { useCurrentUserTenantPermissionsFilter } from 'src/common/permission-hooks';
import { PermissionsConstants } from 'src/common/types';
import { Link } from 'react-router-dom';
import { theme } from 'antd';
const { useToken } = theme;

const permissions = [
  PermissionsConstants.CreateOrganization,
  PermissionsConstants.CreatePartnerOrganization,
  PermissionsConstants.DeleteOrganization,
  PermissionsConstants.DeletePartnerOrganization,
  PermissionsConstants.ManageOrganizationInfo,
  PermissionsConstants.ManageAdminOrganization,

  PermissionsConstants.ManageUsers,
  PermissionsConstants.ManageRoles,
  PermissionsConstants.ManageGroups,
  PermissionsConstants.ManageDefaultRoles,
  PermissionsConstants.ManagePermissions,
  PermissionsConstants.ManageRoleAssignments,
  PermissionsConstants.ManageLicenses,
  PermissionsConstants.ManageVideoTutorialData,
  PermissionsConstants.ViewAllOrganizations,
];

const HomePage: FC = () => {
  const { t } = useTranslation();
  const identity = useIdentity();

  const getName = () => {
    return `${identity?.profile['display_name'] ?? ''}`.trimStart().trimEnd();
  };

  const { token } = useToken();
  const { colorPrimary, colorLink } = token;

  const iconStyle = { color: colorPrimary, margin: 13 };
  const linkStyle = { color: colorLink };

  setHeader(t(`${t('WelcomeBack').replace('{0}', getName())}`));
  setRoutes([]);
  setUsername(getName());

  const [userPermissionsSet, checkingPermissions] = useCurrentUserTenantPermissionsFilter(identity, permissions);

  if (checkingPermissions) {
    return (
      <Row align="top" justify="space-between">
        <Spin spinning size="large" style={{ maxHeight: '75vh' }}>
          <Card style={{ width: '80vw', height: '75vh' }}></Card>
        </Spin>
      </Row>
    );
  }

  return (
    <>
      <div>
        <Menu mode="vertical" style={{ backgroundColor: 'transparent' }}>
          {[PermissionsConstants.ViewAllOrganizations].some((x) => userPermissionsSet.has(x)) && (
            <Menu.Item className="main-page-button">
              <Card className="card-icon">
                <BankOutlined style={iconStyle} />
              </Card>
              <div>
                <Link style={linkStyle} to={`organizations`}>
                  {t('OrganizationsManagement')}
                </Link>
              </div>
            </Menu.Item>
          )}
          {[PermissionsConstants.ManageAdminOrganization].some((x) => userPermissionsSet.has(x)) && (
            <Menu.Item className="main-page-button">
              <Card className="card-icon">
                <CalendarOutlined style={iconStyle} />
              </Card>
              <div>
                <Link style={linkStyle} to={`scheduled-events`}>
                  {t('ScheduledEvents')}
                </Link>
              </div>
            </Menu.Item>
          )}
          {[PermissionsConstants.ManageVideoTutorialData].some((x) => userPermissionsSet.has(x)) && (
            <Menu.Item className="main-page-button">
              <Card className="card-icon">
                <YoutubeOutlined style={iconStyle} />
              </Card>
              <div>
                <Link style={linkStyle} to={`video-tutorials`}>
                  {t('VideoTutorials')}
                </Link>
              </div>
            </Menu.Item>
          )}
          {userPermissionsSet.has(PermissionsConstants.ManageUsers) && (
            <Menu.Item className="main-page-button">
              <Card className="card-icon">
                <UserOutlined style={iconStyle} />
              </Card>
              <Link style={linkStyle} to={`users`}>
                {t('UsersManagement')}
              </Link>
            </Menu.Item>
          )}
          {userPermissionsSet.has(PermissionsConstants.ManageRoles) && (
            <Menu.Item className="main-page-button">
              <Card className="card-icon">
                <AuditOutlined style={iconStyle} />
              </Card>
              <Link style={linkStyle} to={`roles`}>
                {t('RolesManagement')}
              </Link>
            </Menu.Item>
          )}
          {userPermissionsSet.has(PermissionsConstants.ManageRoles) && (
            <Menu.Item className="main-page-button">
              <Card className="card-icon">
                <FileProtectOutlined style={iconStyle} />
              </Card>
              <Link style={linkStyle} to={`adminroles`}>
                {t('AdminRolesManagement')}
              </Link>
            </Menu.Item>
          )}
          {userPermissionsSet.has(PermissionsConstants.ManageGroups) && (
            <Menu.Item className="main-page-button">
              <Card className="card-icon">
                <TeamOutlined style={iconStyle} />
              </Card>
              <Link style={linkStyle} to={`groups`}>
                {t('GroupsManagement')}
              </Link>
            </Menu.Item>
          )}
          {userPermissionsSet.has(PermissionsConstants.ManagePermissions) && (
            <Menu.Item className="main-page-button">
              <Card className="card-icon">
                <LockOutlined style={iconStyle} />
              </Card>
              <Link style={linkStyle} to={`permissions`}>
                {t('PermissionsManagement')}
              </Link>
            </Menu.Item>
          )}
          {userPermissionsSet.has(PermissionsConstants.ManageDefaultRoles) && (
            <Menu.Item className="main-page-button">
              <Card className="card-icon">
                <FileDoneOutlined style={iconStyle} />
              </Card>
              <Link style={linkStyle} to={`default-roles`}>
                {t('DefaultRolesManagement')}
              </Link>
            </Menu.Item>
          )}
          {userPermissionsSet.has(PermissionsConstants.ManageRoleAssignments) && (
            <Menu.Item className="main-page-button">
              <Card className="card-icon">
                <IdcardOutlined style={iconStyle} />
              </Card>
              <Link style={linkStyle} to={`role-assignments`}>
                {t('RoleAssignmentsManagement')}
              </Link>
            </Menu.Item>
          )}
          {userPermissionsSet.has(PermissionsConstants.ManageUsers) && (
            <Menu.Item className="main-page-button">
              <Card className="card-icon">
                <MonitorOutlined style={iconStyle} />
              </Card>
              <Link style={linkStyle} to={`audit-logs`}>
                {t('AuditLogs')}
              </Link>
            </Menu.Item>
          )}
          {identity.email === 'admin@tempuri.org' && (
            <Menu.Item className="main-page-button">
              <Card className="card-icon">
                <FileTextOutlined style={iconStyle} />
              </Card>
              <Link style={linkStyle} to={`dev-logs`}>
                {t('DevLogs')}
              </Link>
            </Menu.Item>
          )}
          {userPermissionsSet.has(PermissionsConstants.ManageGlobalLicenses) && (
            <Menu.Item className="main-page-button">
              <Card className="card-icon">
                <KeyOutlined style={iconStyle} />
              </Card>
              <Link style={linkStyle} to={`licenses`}>
                {t('Licenses')}
              </Link>
            </Menu.Item>
          )}
          {[PermissionsConstants.ManageAdminOrganization].some((x) => userPermissionsSet.has(x)) && (
            <Menu.Item className="main-page-button">
              <Card className="card-icon">
                <FundOutlined style={iconStyle} />
              </Card>
              <div>
                <Link style={linkStyle} to={`statistics`}>
                  {t('Statistics')}
                </Link>
              </div>
            </Menu.Item>
          )}

          {userPermissionsSet.has(PermissionsConstants.ManageAdminOrganization) && (
            <Menu.Item className="main-page-button">
              <Card className="card-icon">
                <MobileOutlined style={iconStyle} />
              </Card>
              <Link style={linkStyle} to={`clients`}>
                {t('Clients')}
              </Link>
            </Menu.Item>
          )}
        </Menu>
      </div>
    </>
  );
};

export default HomePage;
